/**
 * Accessibility utilities for the chat application
 */

// ARIA live region types
export type LiveRegionPoliteness = 'polite' | 'assertive' | 'off';

// Keyboard navigation constants
export const KEYBOARD_KEYS = {
  ENTER: 'Enter',
  ESCAPE: 'Escape',
  SPACE: ' ',
  TAB: 'Tab',
  ARROW_UP: 'ArrowUp',
  ARROW_DOWN: 'ArrowDown',
  ARROW_LEFT: 'ArrowLeft',
  ARROW_RIGHT: 'ArrowRight',
  HOME: 'Home',
  END: 'End',
} as const;

// Focus management utilities
export class FocusManager {
  private static focusableSelectors = [
    'button:not([disabled])',
    'input:not([disabled])',
    'textarea:not([disabled])',
    'select:not([disabled])',
    'a[href]',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable="true"]',
  ].join(', ');

  /**
   * Get all focusable elements within a container
   */
  static getFocusableElements(container: HTMLElement): HTMLElement[] {
    return Array.from(container.querySelectorAll(this.focusableSelectors));
  }

  /**
   * Set focus to the first focusable element in a container
   */
  static focusFirst(container: HTMLElement): boolean {
    const focusable = this.getFocusableElements(container);
    if (focusable.length > 0) {
      focusable[0].focus();
      return true;
    }
    return false;
  }

  /**
   * Set focus to the last focusable element in a container
   */
  static focusLast(container: HTMLElement): boolean {
    const focusable = this.getFocusableElements(container);
    if (focusable.length > 0) {
      focusable[focusable.length - 1].focus();
      return true;
    }
    return false;
  }

  /**
   * Trap focus within a container (for modals, etc.)
   */
  static trapFocus(container: HTMLElement, event: KeyboardEvent): void {
    if (event.key !== KEYBOARD_KEYS.TAB) return;

    const focusable = this.getFocusableElements(container);
    if (focusable.length === 0) return;

    const firstFocusable = focusable[0];
    const lastFocusable = focusable[focusable.length - 1];

    if (event.shiftKey) {
      // Shift + Tab
      if (document.activeElement === firstFocusable) {
        event.preventDefault();
        lastFocusable.focus();
      }
    } else {
      // Tab
      if (document.activeElement === lastFocusable) {
        event.preventDefault();
        firstFocusable.focus();
      }
    }
  }
}

// Live region announcer for screen readers
export class LiveRegionAnnouncer {
  private static instance: LiveRegionAnnouncer;
  private politeRegion: HTMLElement | null = null;
  private assertiveRegion: HTMLElement | null = null;

  private constructor() {
    this.createLiveRegions();
  }

  static getInstance(): LiveRegionAnnouncer {
    if (!this.instance) {
      this.instance = new LiveRegionAnnouncer();
    }
    return this.instance;
  }

  private createLiveRegions(): void {
    // Create polite live region
    this.politeRegion = document.createElement('div');
    this.politeRegion.setAttribute('aria-live', 'polite');
    this.politeRegion.setAttribute('aria-atomic', 'true');
    this.politeRegion.setAttribute('aria-relevant', 'additions text');
    this.politeRegion.className = 'sr-only';
    this.politeRegion.id = 'live-region-polite';

    // Create assertive live region
    this.assertiveRegion = document.createElement('div');
    this.assertiveRegion.setAttribute('aria-live', 'assertive');
    this.assertiveRegion.setAttribute('aria-atomic', 'true');
    this.assertiveRegion.setAttribute('aria-relevant', 'additions text');
    this.assertiveRegion.className = 'sr-only';
    this.assertiveRegion.id = 'live-region-assertive';

    // Add to document
    document.body.appendChild(this.politeRegion);
    document.body.appendChild(this.assertiveRegion);
  }

  /**
   * Announce a message to screen readers
   */
  announce(message: string, politeness: LiveRegionPoliteness = 'polite'): void {
    const region = politeness === 'assertive' ? this.assertiveRegion : this.politeRegion;
    
    if (region) {
      // Clear previous message
      region.textContent = '';
      
      // Add new message after a brief delay to ensure it's announced
      setTimeout(() => {
        region.textContent = message;
      }, 100);

      // Clear message after announcement to avoid repetition
      setTimeout(() => {
        region.textContent = '';
      }, 1000);
    }
  }

  /**
   * Announce new message arrival
   */
  announceNewMessage(sender: string, content: string): void {
    const message = `New message from ${sender}: ${content}`;
    this.announce(message, 'polite');
  }

  /**
   * Announce connection status changes
   */
  announceConnectionStatus(status: string): void {
    const message = `Connection status: ${status}`;
    this.announce(message, 'assertive');
  }

  /**
   * Announce typing indicators
   */
  announceTyping(user: string, isTyping: boolean): void {
    const message = isTyping ? `${user} is typing` : `${user} stopped typing`;
    this.announce(message, 'polite');
  }
}

// Screen reader utilities
export const ScreenReaderUtils = {
  /**
   * Create screen reader only text
   */
  createSROnlyText(text: string): HTMLSpanElement {
    const span = document.createElement('span');
    span.className = 'sr-only';
    span.textContent = text;
    return span;
  },

  /**
   * Format timestamp for screen readers
   */
  formatTimestampForSR(timestamp: Date): string {
    const now = new Date();
    const diff = now.getTime() - timestamp.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) {
      return `${days} day${days > 1 ? 's' : ''} ago`;
    } else if (hours > 0) {
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else if (minutes > 0) {
      return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    } else {
      return 'Just now';
    }
  },

  /**
   * Format message content for screen readers
   */
  formatMessageForSR(sender: string, content: string, timestamp: Date): string {
    const timeStr = this.formatTimestampForSR(timestamp);
    return `Message from ${sender}, ${timeStr}: ${content}`;
  },
};

// Keyboard shortcut manager
export class KeyboardShortcutManager {
  private shortcuts: Map<string, () => void> = new Map();

  /**
   * Register a keyboard shortcut
   */
  register(key: string, modifiers: string[], callback: () => void): void {
    const shortcutKey = this.createShortcutKey(key, modifiers);
    this.shortcuts.set(shortcutKey, callback);
  }

  /**
   * Handle keyboard events
   */
  handleKeyDown(event: KeyboardEvent): boolean {
    const modifiers: string[] = [];
    if (event.ctrlKey) modifiers.push('ctrl');
    if (event.altKey) modifiers.push('alt');
    if (event.shiftKey) modifiers.push('shift');
    if (event.metaKey) modifiers.push('meta');

    const shortcutKey = this.createShortcutKey(event.key, modifiers);
    const callback = this.shortcuts.get(shortcutKey);

    if (callback) {
      event.preventDefault();
      callback();
      return true;
    }

    return false;
  }

  private createShortcutKey(key: string, modifiers: string[]): string {
    return [...modifiers.sort(), key.toLowerCase()].join('+');
  }

  /**
   * Clear all shortcuts
   */
  clear(): void {
    this.shortcuts.clear();
  }
}

// Color contrast utilities
export const ColorContrastUtils = {
  /**
   * Calculate relative luminance of a color
   */
  getRelativeLuminance(r: number, g: number, b: number): number {
    const [rs, gs, bs] = [r, g, b].map(c => {
      c = c / 255;
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
  },

  /**
   * Calculate contrast ratio between two colors
   */
  getContrastRatio(color1: [number, number, number], color2: [number, number, number]): number {
    const l1 = this.getRelativeLuminance(...color1);
    const l2 = this.getRelativeLuminance(...color2);
    const lighter = Math.max(l1, l2);
    const darker = Math.min(l1, l2);
    return (lighter + 0.05) / (darker + 0.05);
  },

  /**
   * Check if contrast ratio meets WCAG AA standards
   */
  meetsWCAGAA(contrastRatio: number, isLargeText: boolean = false): boolean {
    return contrastRatio >= (isLargeText ? 3 : 4.5);
  },

  /**
   * Check if contrast ratio meets WCAG AAA standards
   */
  meetsWCAGAAA(contrastRatio: number, isLargeText: boolean = false): boolean {
    return contrastRatio >= (isLargeText ? 4.5 : 7);
  },
};